(() => {
  'use strict';
  if (customElements.get('lmd-cart-promo')) return;
  const root = window.Shopify?.routes?.root || '/';
  let cachedCart;
  let cartRead;
  let busy = false;
  let message = '';
  let errorMessage = false;
  const codes = cart => (cart.discount_codes || []).map(entry => ({ code: entry.code, applicable: entry.applicable === true }));
  const sameCode = (a, b) => a.toLocaleLowerCase() === b.toLocaleLowerCase();
  async function readCart() {
    if (!cartRead) cartRead = fetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(response => { if (!response.ok) throw new Error('Cart unavailable'); return response.json(); })
      .finally(() => { cartRead = null; });
    return cartRead;
  }
  function sectionTargets() {
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    return [
      { id: 'cart-drawer', target: 'cart-drawer .drawer__inner', source: '.drawer__inner' },
      { id: 'cart-icon-bubble', target: '#cart-icon-bubble', source: '.shopify-section' },
      ...(items?.dataset.id ? [{ id: items.dataset.id, target: '#main-cart-items .js-contents', source: '.js-contents' }] : []),
      ...(footer?.dataset.id ? [{ id: footer.dataset.id, target: '#main-cart-footer .js-contents', source: '.js-contents' }] : [])
    ];
  }
  function redraw(cart, targets, restoreInput) {
    cachedCart = cart;
    const drawerContent = document.querySelector('#CartDrawer cart-drawer-items');
    const scrollTop = drawerContent?.scrollTop || 0;
    const drawerOpen = document.querySelector('cart-drawer')?.classList.contains('active');
    for (const { id, target, source } of targets) {
      const current = document.querySelector(target);
      const html = cart.sections?.[id];
      if (!current || !html) continue;
      const replacement = new DOMParser().parseFromString(html, 'text/html').querySelector(source);
      if (replacement) current.innerHTML = replacement.innerHTML;
    }
    document.querySelector('cart-drawer')?.classList.toggle('is-empty', cart.item_count === 0);
    const newContent = document.querySelector('#CartDrawer cart-drawer-items');
    if (newContent) newContent.scrollTop = scrollTop;
    document.querySelectorAll('lmd-cart-promo').forEach(element => element.render());
    const surface = drawerOpen ? document.querySelector('cart-drawer lmd-cart-promo') : document.querySelector('#main-cart-footer lmd-cart-promo');
    if (surface && restoreInput !== undefined) {
      const input = surface.querySelector('input');
      input.value = restoreInput;
      input.focus({ preventScroll: true });
    }
  }
  function setBusy(value) {
    busy = value;
    document.querySelectorAll('lmd-cart-promo').forEach(element => element.render());
    document.querySelectorAll('#LMD-CartDrawer-Checkout, #LMD-Cart-Checkout, #CartDrawer-Checkout, #checkout').forEach(button => {
      button.setAttribute('aria-disabled', String(value));
      if (value) { button.dataset.lmdPromoDisabled = String(button.disabled); button.disabled = true; }
      else if (button.dataset.lmdPromoDisabled !== undefined) {
        button.disabled = button.dataset.lmdPromoDisabled === 'true';
        delete button.dataset.lmdPromoDisabled;
      } else if (cachedCart?.item_count > 0) button.disabled = false;
    });
  }
  class CartPromo extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', event => {
        const apply = event.target.closest('[data-lmd-promo-apply]');
        const remove = event.target.closest('[data-lmd-promo-remove]');
        if (apply || remove) { event.preventDefault(); this.update(remove?.dataset.lmdPromoRemove); }
      });
      this.querySelector('input').addEventListener('keydown', event => {
        if (event.key === 'Enter') { event.preventDefault(); this.update(); }
      });
      this.render();
      if (!busy) readCart().then(cart => { cachedCart = cart; document.querySelectorAll('lmd-cart-promo').forEach(element => element.render()); }).catch(() => {});
    }
    render() {
      this.querySelectorAll('button, input').forEach(control => { control.disabled = busy; });
      this.setAttribute('aria-busy', String(busy));
      const status = this.querySelector('.lmd-promo__status');
      status.textContent = busy ? this.dataset.busy : message;
      status.toggleAttribute('data-error', errorMessage && !busy);
      const list = this.querySelector('.lmd-promo__codes');
      list.replaceChildren();
      if (!cachedCart) return;
      for (const entry of codes(cachedCart)) {
        const item = document.createElement('li');
        item.toggleAttribute('data-unavailable', !entry.applicable);
        const label = document.createElement('span');
        label.textContent = entry.code + (entry.applicable ? '' : ' · ' + this.dataset.unavailable);
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.dataset.lmdPromoRemove = entry.code;
        remove.setAttribute('aria-label', this.dataset.remove + ' ' + entry.code);
        remove.textContent = '×';
        remove.disabled = busy;
        item.append(label, remove);
        list.append(item);
      }
    }
    async update(removeCode) {
      if (busy) return;
      const input = this.querySelector('input');
      const newCode = input.value.trim();
      if (removeCode === undefined && (!newCode || newCode.includes(','))) {
        message = this.dataset.empty; errorMessage = true; this.render(); return;
      }
      const labels = { ...this.dataset };
      const targets = sectionTargets();
      setBusy(true);
      let restoreInput = newCode;
      try {
        const original = await readCart();
        const previous = codes(original).map(entry => entry.code);
        const desired = removeCode !== undefined ? previous.filter(code => !sameCode(code, removeCode))
          : previous.some(code => sameCode(code, newCode)) ? previous : [...previous, newCode];
        const send = async values => {
          const response = await fetch(root + 'cart/update.js', {
            method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ discount: values.join(','), sections: targets.map(target => target.id), sections_url: location.pathname })
          });
          const cart = await response.json();
          if (!response.ok || cart.errors) throw new Error('Promotion unavailable');
          return cart;
        };
        let cart = await send(desired);
        const applicable = removeCode !== undefined || codes(cart).some(entry => sameCode(entry.code, newCode) && entry.applicable);
        if (!applicable && !previous.some(code => sameCode(code, newCode))) cart = await send(previous);
        message = removeCode !== undefined ? labels.removed : applicable ? labels.applied : labels.invalid;
        errorMessage = !applicable;
        if (applicable) restoreInput = '';
        redraw(cart, targets, restoreInput);
      } catch (_) {
        message = labels.error; errorMessage = true;
      } finally {
        setBusy(false);
        const surface = document.querySelector('cart-drawer.active lmd-cart-promo') || document.querySelector('#main-cart-footer lmd-cart-promo');
        if (surface) { const field = surface.querySelector('input'); field.value = restoreInput; field.focus({ preventScroll: true }); }
      }
    }
  }
  document.addEventListener('click', event => {
    if (busy && event.target.closest('#LMD-CartDrawer-Checkout, #LMD-Cart-Checkout, #CartDrawer-Checkout, #checkout')) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  customElements.define('lmd-cart-promo', CartPromo);
})();

